import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  prequalificationApi,
  type PrequalInviteResolution,
} from "../../api/prequalification";

/**
 * Resolves the `?invite={token}` on the public funnel, once, on landing.
 *
 * Shared by the buyer and seller pages so the two cannot drift on what a dead
 * link looks like. Returns four things the pages need and nothing they don't:
 * the raw `token` (threaded into finalize), the resolved `invite`, a `pending`
 * flag, and `isDead` — true only once we KNOW the link is bad, never while the
 * lookup is still in flight.
 *
 * Failure posture is deliberately generous. A network error, a rate limit, or
 * a malformed response leaves `isDead` FALSE and the funnel behaves exactly as
 * it does for organic traffic — the token still rides along to finalize, where
 * the server makes the authoritative call. Blocking a real invitee behind a
 * transient lookup failure would be a far worse outcome than losing the
 * prefill, and the server ignores a token it cannot honour anyway.
 */
export interface PrequalInviteState {
  /** The raw token, or null when the URL carries none. Pass to finalize. */
  token: string | null;
  /** The resolution, or null while pending / on a lookup failure. */
  invite: PrequalInviteResolution | null;
  /** True while the lookup is in flight — the pages hold the form until it
   *  settles so a prefill never lands after the user has started typing. */
  pending: boolean;
  /** True ONLY when the server said the link is not live. */
  isDead: boolean;
}

export function usePrequalInvite(): PrequalInviteState {
  const [params] = useSearchParams();
  const token = params.get("invite");

  const [invite, setInvite] = useState<PrequalInviteResolution | null>(null);
  const [pending, setPending] = useState<boolean>(!!token);

  useEffect(() => {
    if (!token) {
      setInvite(null);
      setPending(false);
      return;
    }

    let cancelled = false;
    setPending(true);
    void (async () => {
      try {
        const result = await prequalificationApi.resolveInvite(token);
        if (!cancelled) setInvite(result);
      } catch {
        // Generous on purpose — see the doc above. The token still rides to
        // finalize; the server is the authority on whether to honour it.
        if (!cancelled) setInvite(null);
      } finally {
        if (!cancelled) setPending(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  return {
    token,
    invite,
    pending,
    isDead: invite !== null && !invite.isLive,
  };
}
